import { useState, useEffect, useRef, useMemo } from 'react';
import {
  createTypesenseClient,
  getCollectionNames,
  getTypesenseSearchConfigError,
  TYPESENSE_SEARCH_CONFIG_ERROR,
} from '../utils/typesense';
import { sortFeaturedBeersByBreweryThenName } from '../utils/featuredBeersDisplaySort';

/** Max IDs per Typesense search filter chunk (avoid oversized filter strings). */
const ID_CHUNK_SIZE = 80;

/**
 * Escape a document id for Typesense filter_by when wrapped in backticks.
 * @param {string} rawId
 * @returns {string}
 */
export function escapeTypesenseIdForFilter(rawId) {
  return String(rawId ?? '').replace(/`/g, '``');
}

/**
 * Build filter_by for matching any of the given string ids (field `id`).
 * Uses Typesense IN syntax (one clause) to avoid hitting max filter clause limits
 * versus chaining `id:=\`...\` || id:=\`...\``.
 * @see https://typesense.org/docs/28.0/api/search.html#filter-parameters
 * @param {string[]} ids - Non-empty trimmed ids
 * @returns {string}
 */
export function buildBeerIdsFilterBy(ids) {
  const literals = ids.map((beerId) => {
    const escaped = escapeTypesenseIdForFilter(beerId);
    return `\`${escaped}\``;
  });
  return `id:=[${literals.join(', ')}]`;
}

function getEnvModeForTypesense() {
  const mode = (import.meta.env.MODE || 'development').toLowerCase();
  return ['development', 'staging', 'production'].includes(mode)
    ? mode
    : 'development';
}

/**
 * Fetch beer documents from Typesense by id list; merges chunked responses.
 * @param {import('typesense').Client} client
 * @param {string} collectionName
 * @param {string[]} uniqueIds
 * @returns {Promise<object[]>} Raw hit documents (unsorted)
 */
export async function fetchBeersByIdsFromTypesense(client, collectionName, uniqueIds) {
  if (uniqueIds.length === 0) {
    return [];
  }

  const chunks = [];
  for (let index = 0; index < uniqueIds.length; index += ID_CHUNK_SIZE) {
    chunks.push(uniqueIds.slice(index, index + ID_CHUNK_SIZE));
  }

  const merged = [];
  for (const chunk of chunks) {
    const filterBy = buildBeerIdsFilterBy(chunk);
    const searchResponse = await client
      .collections(collectionName)
      .documents()
      .search({
        q: '*',
        query_by: 'name',
        filter_by: filterBy,
        per_page: Math.min(250, chunk.length),
        page: 1,
      });

    const hits = searchResponse?.hits ?? [];
    for (const hit of hits) {
      const doc = hit.document ?? hit;
      merged.push({
        id: doc.id ?? hit.id,
        ...doc,
      });
    }
  }

  return merged;
}

/**
 * Load featured beer rows from Typesense for the given Firestore beer ids.
 * Returns sorted beers (brewery then name), missing ids, loading, and error.
 *
 * @param {string[]} beerIds - Firestore / Typesense beer document ids
 * @param {number} [reloadKey] - Increment to refetch the same ids (e.g. after editing a beer)
 */
export function useFeaturedBeersDocuments(beerIds, reloadKey = 0) {
  const normalizedIds = useMemo(() => {
    if (!Array.isArray(beerIds)) {
      return [];
    }
    const unique = [...new Set(beerIds.map((beerId) => String(beerId ?? '').trim()).filter(Boolean))];
    unique.sort((left, right) => left.localeCompare(right));
    return unique;
  }, [beerIds]);

  const idsKey = normalizedIds.join('\u0001');
  const fetchKey = `${idsKey}\u0002${reloadKey}`;

  const [beers, setBeers] = useState([]);
  const [missingIds, setMissingIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!isMountedRef.current) return;

      setLoading(true);
      setError(null);

      if (normalizedIds.length === 0) {
        if (!cancelled && isMountedRef.current) {
          setBeers([]);
          setMissingIds([]);
          setLoading(false);
        }
        return;
      }

      const configError = getTypesenseSearchConfigError();
      if (configError) {
        if (!cancelled && isMountedRef.current) {
          setBeers([]);
          setMissingIds([...normalizedIds]);
          setError(configError);
          setLoading(false);
        }
        return;
      }

      const env = getEnvModeForTypesense();
      const { beer: collectionName } = getCollectionNames(env);

      if (!collectionName) {
        if (!cancelled && isMountedRef.current) {
          setBeers([]);
          setMissingIds([...normalizedIds]);
          setError(TYPESENSE_SEARCH_CONFIG_ERROR);
          setLoading(false);
        }
        return;
      }

      const client = createTypesenseClient();
      if (!client) {
        if (!cancelled && isMountedRef.current) {
          setBeers([]);
          setMissingIds([...normalizedIds]);
          setError(TYPESENSE_SEARCH_CONFIG_ERROR);
          setLoading(false);
        }
        return;
      }

      try {
        const fetched = await fetchBeersByIdsFromTypesense(
          client,
          collectionName,
          normalizedIds,
        );

        if (cancelled || !isMountedRef.current) return;

        const foundSet = new Set(fetched.map((row) => String(row.id ?? '').trim()));
        const missing = normalizedIds.filter((beerId) => !foundSet.has(beerId));
        const sorted = sortFeaturedBeersByBreweryThenName(fetched);

        setBeers(sorted);
        setMissingIds(missing);
      } catch (err) {
        if (cancelled || !isMountedRef.current) return;
        // eslint-disable-next-line no-console
        console.error('useFeaturedBeersDocuments', err);
        setBeers([]);
        setMissingIds([...normalizedIds]);
        setError(err?.message || 'Failed to load featured beers.');
      } finally {
        if (!cancelled && isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [fetchKey]);

  return { beers, loading, error, missingIds };
}

export default useFeaturedBeersDocuments;
